import React from 'react';
import { Vocabulary, Concept } from '@semantical/modules/vocabulary';

// SKOS vocabulary URIs
const SKOS = {
    ConceptScheme: 'http://www.w3.org/2004/02/skos/core#ConceptScheme',
    prefLabel: 'http://www.w3.org/2004/02/skos/core#prefLabel',
    altLabel: 'http://www.w3.org/2004/02/skos/core#altLabel',
    hiddenLabel: 'http://www.w3.org/2004/02/skos/core#hiddenLabel',
    definition: 'http://www.w3.org/2004/02/skos/core#definition',
    scopeNote: 'http://www.w3.org/2004/02/skos/core#scopeNote',
    example: 'http://www.w3.org/2004/02/skos/core#example',
    note: 'http://www.w3.org/2004/02/skos/core#note',
    topConceptOf: 'http://www.w3.org/2004/02/skos/core#topConceptOf',
    hasTopConcept: 'http://www.w3.org/2004/02/skos/core#hasTopConcept',
};

// RDFS vocabulary URIs
const RDFS = {
    label: 'http://www.w3.org/2000/01/rdf-schema#label',
    comment: 'http://www.w3.org/2000/01/rdf-schema#comment',
};

// DC Terms vocabulary URIs
const DCTERMS = {
    title: 'http://purl.org/dc/terms/title',
    description: 'http://purl.org/dc/terms/description',
    created: 'http://purl.org/dc/terms/created',
    modified: 'http://purl.org/dc/terms/modified',
    creator: 'http://purl.org/dc/terms/creator',
    publisher: 'http://purl.org/dc/terms/publisher',
    license: 'http://purl.org/dc/terms/license',
    rights: 'http://purl.org/dc/terms/rights',
    source: 'http://purl.org/dc/terms/source',
};

type LabelInfo = {
    value: string;
    typeUri: string;   // Full URI of the property
    typeLabel: string; // Human-readable label from ontology
    language?: string; // e.g., 'en', 'fr'
};

type DefinitionInfo = {
    value: string;
    typeUri: string;   // Full URI of the property
    typeLabel: string; // Human-readable label from ontology
    language?: string;
};

type TextSegment =
    | { type: 'text'; content: string }
    | { type: 'link'; content: string; conceptUri: string }
    | { type: 'self'; content: string };

type IndexEntry = {
    term: string;
    conceptUri: string;
    labelType: 'pref' | 'alt' | 'hidden' | 'rdfs';
    prefLabel: string;  // The concept's preferred label (for showing "alt → pref")
};

type Props = {
    vocabulary: Vocabulary;
    onConceptSelected?: (concept: Concept) => void;
    selectedConceptUri?: string | null;
}

type State = {
    copyFeedback: string | null;
    viewMode: 'alphabetical' | 'hierarchical';
    termMapReady: boolean;
    collapsedLetters: Set<string>;
    expandedNodes: Set<string>;
    scrollTop: number;
    containerHeight: number;
}

// Virtualization constants
const CONCEPT_CARD_HEIGHT = 200;  // Average card height estimate (includes margin)
const OVERSCAN_COUNT = 5;         // Extra items above/below viewport
const VIRTUALIZATION_THRESHOLD = 200;  // Only virtualize when more concepts than this

class ConceptSchemeViewer extends React.Component<Props, State> {
    private conceptRefs: Map<string, HTMLDivElement> = new Map();      // Tree node refs
    private conceptCardRefs: Map<string, HTMLDivElement> = new Map();  // Concept card refs
    private letterRefs: Map<string, HTMLDivElement> = new Map();
    private cardsContainerRef = React.createRef<HTMLDivElement>();     // Cards panel scroll container
    private indexContainerRef = React.createRef<HTMLDivElement>();

    // Class-level caches for expensive computations
    private termMap: Map<string, string> | null = null;
    private alphabeticalIndex: Map<string, IndexEntry[]> | null = null;
    private sortedConceptsCache: Concept[] | null = null;
    private sortedConceptsCacheKey: number = 0;

    constructor(props: Props) {
        super(props);
        this.state = {
            copyFeedback: null,
            viewMode: 'alphabetical',
            termMapReady: false,
            collapsedLetters: new Set<string>(),
            expandedNodes: new Set<string>(),
            scrollTop: 0,
            containerHeight: 600
        };
    }

    async componentDidMount() {
        // Ensure SKOS ontology is loaded into the vocabulary store
        await this.props.vocabulary.ensureSkosOntologyLoaded();

        // Build expensive data structures once (can be slow for large vocabularies)
        this.termMap = this.buildTermToConceptMap();
        this.alphabeticalIndex = this.buildAlphabeticalIndex();

        // Start with all letters collapsed for large vocabularies
        const concepts = this.props.vocabulary.getConcepts();
        const initialCollapsed = new Set<string>();
        if (concepts.length > 500) {
            // Collapse all letters for large vocabularies
            this.alphabeticalIndex.forEach((_, letter) => initialCollapsed.add(letter));
        }

        this.setState({
            termMapReady: true,
            collapsedLetters: initialCollapsed
        });
    }

    componentDidUpdate(prevProps: Props) {
        // If vocabulary changed or has more concepts now, rebuild indexes
        const prevConceptCount = prevProps.vocabulary.getConcepts().length;
        const currentConceptCount = this.props.vocabulary.getConcepts().length;

        if (prevProps.vocabulary !== this.props.vocabulary || prevConceptCount !== currentConceptCount) {
            // Rebuild indexes when vocabulary changes
            this.termMap = this.buildTermToConceptMap();
            this.alphabeticalIndex = this.buildAlphabeticalIndex();
            this.sortedConceptsCache = null; // Invalidate cache

            // Update collapsed state for large vocabularies
            if (currentConceptCount > 500 && prevConceptCount <= 500) {
                const initialCollapsed = new Set<string>();
                this.alphabeticalIndex.forEach((_, letter) => initialCollapsed.add(letter));
                this.setState({ collapsedLetters: initialCollapsed });
            }

            // Force re-render with new data
            this.forceUpdate();
        }
    }

    /**
     * Get the human-readable label for a property URI from the SKOS ontology.
     * Falls back to extracting the local name if ontology not loaded.
     */
    private getPropertyLabel(propertyUri: string): string {
        const { vocabulary } = this.props;
        const label = vocabulary.getSkosPropertyLabel(propertyUri);
        if (label) return label;

        // Fallback: extract local name from URI
        const hashIndex = propertyUri.lastIndexOf('#');
        if (hashIndex !== -1) return propertyUri.substring(hashIndex + 1);
        const slashIndex = propertyUri.lastIndexOf('/');
        if (slashIndex !== -1) return propertyUri.substring(slashIndex + 1);
        return propertyUri;
    }

    /**
     * Get all labels for a concept from the RDF store.
     * Returns labels with their type URI, human-readable label, and language tag.
     */
    private getLabels(conceptUri: string): LabelInfo[] {
        const { vocabulary } = this.props;
        const literalValues = vocabulary.getConceptLabels(conceptUri);

        return literalValues.map(lv => ({
            value: lv.value,
            typeUri: lv.predicateUri,
            typeLabel: this.getPropertyLabel(lv.predicateUri),
            language: lv.language
        }));
    }

    /**
     * Get all definitions/notes for a concept from the RDF store.
     * Returns definitions with their type URI, human-readable label, and language tag.
     */
    private getDefinitions(conceptUri: string): DefinitionInfo[] {
        const { vocabulary } = this.props;
        const literalValues = vocabulary.getConceptDefinitions(conceptUri);

        return literalValues.map(lv => ({
            value: lv.value,
            typeUri: lv.predicateUri,
            typeLabel: this.getPropertyLabel(lv.predicateUri),
            language: lv.language
        }));
    }

    /**
     * Get the ConceptScheme info from the RDF store.
     * Returns the URI, label, Dublin Core metadata, and top concepts of the concept scheme, if found.
     */
    private getConceptSchemeInfo(): {
        uri: string;
        label: string;
        description?: string;
        created?: string;
        modified?: string;
        creator?: string;
        publisher?: string;
        license?: string;
        rights?: string;
        source?: string;
        topConcepts: Array<{ uri: string; label: string }>;
    } | null {
        const { vocabulary } = this.props;

        // Find the ConceptScheme resource
        const schemeUri = vocabulary.getConceptSchemeUri();
        if (!schemeUri) {
            return null;
        }

        // Get all literals for the scheme
        const allPredicates = [
            SKOS.prefLabel, RDFS.label, DCTERMS.title,
            DCTERMS.description, DCTERMS.created, DCTERMS.modified,
            DCTERMS.creator, DCTERMS.publisher, DCTERMS.license,
            DCTERMS.rights, DCTERMS.source
        ];
        const literals = vocabulary.getSubjectLiterals(schemeUri, allPredicates);

        // Helper to get first literal value for a predicate
        const getFirst = (predicate: string): string | undefined => {
            const found = literals.find(l => l.predicateUri === predicate);
            return found?.value;
        };

        // Try to get the label from various properties (in order of preference)
        let label: string = schemeUri; // default fallback
        const labelPredicates = [SKOS.prefLabel, RDFS.label, DCTERMS.title];
        for (const predicate of labelPredicates) {
            const found = getFirst(predicate);
            if (found) {
                label = found;
                break;
            }
        }

        // If no label found from predicates, extract local name from URI
        if (label === schemeUri) {
            const hashIndex = schemeUri.lastIndexOf('#');
            if (hashIndex !== -1) {
                label = schemeUri.substring(hashIndex + 1);
            } else {
                const slashIndex = schemeUri.lastIndexOf('/');
                if (slashIndex !== -1) {
                    label = schemeUri.substring(slashIndex + 1);
                }
            }
        }

        // Get top concepts
        const topConceptUris = vocabulary.getTopConcepts(schemeUri);
        const topConcepts: Array<{ uri: string; label: string }> = topConceptUris.map(uri => {
            const concept = vocabulary.getConcept(uri);
            return { uri, label: concept.prefLabel };
        });

        // Sort alphabetically by label
        topConcepts.sort((a, b) => a.label.localeCompare(b.label));

        return {
            uri: schemeUri,
            label,
            description: getFirst(DCTERMS.description),
            created: getFirst(DCTERMS.created),
            modified: getFirst(DCTERMS.modified),
            creator: getFirst(DCTERMS.creator),
            publisher: getFirst(DCTERMS.publisher),
            license: getFirst(DCTERMS.license),
            rights: getFirst(DCTERMS.rights),
            source: getFirst(DCTERMS.source),
            topConcepts,
        };
    }

    /**
     * Generate common English plural forms of a term.
     */
    private getPluralForms(term: string): string[] {
        const forms = [term];
        const lower = term.toLowerCase();

        // Standard plural: add 's'
        forms.push(term + 's');

        // Words ending in s, x, z, ch, sh: add 'es'
        if (/[sxz]$/.test(lower) || /[sc]h$/.test(lower)) {
            forms.push(term + 'es');
        }

        // Words ending in consonant + y: replace with 'ies'
        if (/[^aeiou]y$/i.test(lower)) {
            forms.push(term.slice(0, -1) + 'ies');
        }

        return forms;
    }

    /**
     * Build a map from all vocabulary terms (prefLabels, altLabels, hiddenLabels)
     * and their plural forms to their concept URIs.
     */
    private buildTermToConceptMap(): Map<string, string> {
        const { vocabulary } = this.props;
        const termMap = new Map<string, string>(); // term (lowercase) → concept URI

        for (const concept of vocabulary.getConcepts()) {
            // Get all labels for this concept
            const labels = vocabulary.getConceptLabels(concept.uri);

            for (const label of labels) {
                // Add the label and its plural forms
                for (const form of this.getPluralForms(label.value)) {
                    termMap.set(form.toLowerCase(), concept.uri);
                }
            }
        }

        return termMap;
    }

    /**
     * Build an alphabetical index of all terms grouped by first letter.
     */
    private buildAlphabeticalIndex(): Map<string, IndexEntry[]> {
        const { vocabulary } = this.props;
        const entries: IndexEntry[] = [];

        for (const concept of vocabulary.getConcepts()) {
            const labels = this.getLabels(concept.uri);
            for (const label of labels) {
                entries.push({
                    term: label.value,
                    conceptUri: concept.uri,
                    labelType: label.typeUri === SKOS.prefLabel ? 'pref'
                             : label.typeUri === SKOS.altLabel ? 'alt'
                             : label.typeUri === SKOS.hiddenLabel ? 'hidden'
                             : 'rdfs',
                    prefLabel: concept.prefLabel
                });
            }
        }

        // Sort alphabetically (case-insensitive)
        entries.sort((a, b) => a.term.localeCompare(b.term, undefined, { sensitivity: 'base' }));

        // Group by first letter
        const index = new Map<string, IndexEntry[]>();
        for (const entry of entries) {
            const firstChar = entry.term.charAt(0).toUpperCase();
            const letter = /[A-Z]/.test(firstChar) ? firstChar : '#';
            const group = index.get(letter) || [];
            group.push(entry);
            index.set(letter, group);
        }

        return index;
    }

    /**
     * Get sorted concepts with caching for performance.
     */
    private getSortedConcepts(): Concept[] {
        const concepts = this.props.vocabulary.getConcepts();

        // Simple cache invalidation based on concept count
        if (this.sortedConceptsCache && this.sortedConceptsCacheKey === concepts.length) {
            return this.sortedConceptsCache;
        }

        this.sortedConceptsCache = [...concepts].sort((a, b) =>
            a.prefLabel.localeCompare(b.prefLabel)
        );
        this.sortedConceptsCacheKey = concepts.length;

        return this.sortedConceptsCache;
    }

    /**
     * Calculate visible range for virtualized list.
     */
    private getVisibleRange(): { startIndex: number; endIndex: number } {
        const { scrollTop, containerHeight } = this.state;
        const sortedConcepts = this.getSortedConcepts();

        const startIndex = Math.max(0, Math.floor(scrollTop / CONCEPT_CARD_HEIGHT) - OVERSCAN_COUNT);
        const endIndex = Math.min(
            sortedConcepts.length,
            Math.ceil((scrollTop + containerHeight) / CONCEPT_CARD_HEIGHT) + OVERSCAN_COUNT
        );

        return { startIndex, endIndex };
    }

    /**
     * Handle scroll event for virtualization.
     */
    private handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        this.setState({
            scrollTop: target.scrollTop,
            containerHeight: target.clientHeight
        });
    }

    /**
     * Toggle collapse state for a letter section in the index.
     */
    private toggleLetterCollapse = (letter: string) => {
        this.setState(state => {
            const collapsedLetters = new Set(state.collapsedLetters);
            if (collapsedLetters.has(letter)) {
                collapsedLetters.delete(letter);
            } else {
                collapsedLetters.add(letter);
            }
            return { collapsedLetters };
        });
    }

    /**
     * Toggle expand/collapse state for a tree node in hierarchical view.
     */
    private toggleNode = (uri: string) => {
        this.setState(state => {
            const expandedNodes = new Set(state.expandedNodes);
            if (expandedNodes.has(uri)) {
                expandedNodes.delete(uri);
            } else {
                expandedNodes.add(uri);
            }
            return { expandedNodes };
        });
    }

    /**
     * Handle click on a letter in the index navigation.
     */
    private handleLetterClick = (letter: string) => {
        const letterRef = this.letterRefs.get(letter);
        if (letterRef && this.indexContainerRef.current) {
            letterRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /**
     * Get total term count from alphabetical index.
     */
    private getTotalTermCount(index: Map<string, IndexEntry[]>): number {
        let count = 0;
        index.forEach(entries => count += entries.length);
        return count;
    }

    /**
     * Render a single index entry.
     */
    private renderIndexEntry(entry: IndexEntry, idx: number): React.ReactNode {
        const isAlt = entry.labelType !== 'pref';
        const showArrow = isAlt && entry.term !== entry.prefLabel;

        return (
            <div
                key={`${entry.conceptUri}-${entry.term}-${idx}`}
                className="text-[11px] py-0.5"
                title={showArrow ? `${entry.term} → ${entry.prefLabel}` : entry.term}
            >
                {showArrow ? (
                    <div className="leading-tight">
                        <span className="text-slate-500 dark:text-white/50 italic">
                            {entry.term}
                        </span>
                        <span className="text-slate-400 dark:text-white/30 mx-1">→</span>
                        <span
                            onClick={(e) => this.handleRelatedConceptClick(entry.conceptUri, e)}
                            className="text-slate-700 dark:text-white/80 hover:text-accent-teal dark:hover:text-accent cursor-pointer"
                        >
                            {entry.prefLabel}
                        </span>
                    </div>
                ) : (
                    <span
                        onClick={(e) => this.handleRelatedConceptClick(entry.conceptUri, e)}
                        className="text-slate-700 dark:text-white/80 hover:text-accent-teal dark:hover:text-accent cursor-pointer"
                    >
                        {entry.term}
                    </span>
                )}
            </div>
        );
    }

    /**
     * Render the alphabetical term index content (without outer wrapper).
     */
    private renderTermIndexContent(index: Map<string, IndexEntry[]>): React.ReactNode {
        const letters = Array.from(index.keys()).sort();
        const { collapsedLetters } = this.state;
        const totalTerms = this.getTotalTermCount(index);

        return (
            <>
                {/* Letter navigation header */}
                <div className="px-3 pt-3 pb-2 border-b border-slate-200 dark:border-white/10 flex-shrink-0">
                    <div className="text-[10px] text-slate-400 dark:text-white/40 uppercase tracking-wider mb-2 font-medium">
                        Term Index ({totalTerms.toLocaleString()} terms)
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {letters.map(letter => {
                            const count = index.get(letter)?.length || 0;
                            const isCollapsed = collapsedLetters.has(letter);
                            return (
                                <span
                                    key={letter}
                                    onClick={() => this.handleLetterClick(letter)}
                                    className={`text-[11px] px-1.5 py-0.5 rounded cursor-pointer transition-colors ${
                                        isCollapsed
                                            ? 'bg-slate-200 dark:bg-white/20 text-slate-500 dark:text-white/50'
                                            : 'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-white/70 hover:bg-accent-teal/20 hover:text-accent-teal dark:hover:bg-accent/20 dark:hover:text-accent'
                                    }`}
                                    title={`${letter}: ${count} terms (click to scroll)`}
                                >
                                    {letter}
                                </span>
                            );
                        })}
                    </div>
                </div>

                {/* Scrollable index content with collapsible sections */}
                <div ref={this.indexContainerRef} className="flex-1 overflow-y-auto p-3">
                    {letters.map(letter => {
                        const isCollapsed = collapsedLetters.has(letter);
                        const entries = index.get(letter)!;

                        return (
                            <div
                                key={letter}
                                ref={(el) => { if (el) this.letterRefs.set(letter, el); }}
                                className="mb-3 scroll-mt-2"
                            >
                                <div
                                    className="flex items-center gap-2 cursor-pointer hover:text-accent-teal dark:hover:text-accent transition-colors"
                                    onClick={() => this.toggleLetterCollapse(letter)}
                                >
                                    <svg
                                        width="10" height="10" viewBox="0 0 10 10"
                                        className={`transition-transform text-slate-400 dark:text-white/40 ${isCollapsed ? '' : 'rotate-90'}`}
                                    >
                                        <path d="M3,1 L7,5 L3,9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                    <span className="text-[11px] font-semibold text-slate-500 dark:text-white/50">
                                        {letter} ({entries.length})
                                    </span>
                                </div>

                                {!isCollapsed && (
                                    <div className="ml-4 mt-1">
                                        {entries.map((entry, idx) => this.renderIndexEntry(entry, idx))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </>
        );
    }

    /**
     * Parse definition text and return an array of segments (plain text, clickable terms, or self-references).
     * Self-references are highlighted but not clickable.
     */
    private parseDefinitionText(
        text: string,
        termMap: Map<string, string>,
        currentConceptUri: string
    ): TextSegment[] {
        // Get all terms, sorted by length (longest first) to match "Bounded Context" before "Context"
        const terms = Array.from(termMap.keys()).sort((a, b) => b.length - a.length);

        // Build regex pattern with word boundaries
        // Escape special regex characters in terms
        const escapedTerms = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        if (escapedTerms.length === 0) {
            return [{ type: 'text', content: text }];
        }

        const pattern = new RegExp(`\\b(${escapedTerms.join('|')})\\b`, 'gi');

        const segments: TextSegment[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = pattern.exec(text)) !== null) {
            const matchedTerm = match[1];
            const conceptUri = termMap.get(matchedTerm.toLowerCase());

            // Add text before the match
            if (match.index > lastIndex) {
                segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
            }

            // Add self-reference or link
            if (conceptUri === currentConceptUri) {
                segments.push({ type: 'self', content: matchedTerm });
            } else if (conceptUri) {
                segments.push({ type: 'link', content: matchedTerm, conceptUri });
            } else {
                segments.push({ type: 'text', content: matchedTerm });
            }

            lastIndex = pattern.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
            segments.push({ type: 'text', content: text.slice(lastIndex) });
        }

        return segments.length > 0 ? segments : [{ type: 'text', content: text }];
    }

    /**
     * Render definition text with clickable links for vocabulary terms.
     * Self-references are highlighted but not clickable.
     */
    private renderDefinitionWithLinks(
        text: string,
        termMap: Map<string, string>,
        currentConceptUri: string
    ): React.ReactNode {
        const segments = this.parseDefinitionText(text, termMap, currentConceptUri);
        return segments.map((segment, idx) => {
            if (segment.type === 'text') {
                return <span key={idx}>{segment.content}</span>;
            }
            if (segment.type === 'self') {
                // Self-reference: highlighted but not clickable
                return (
                    <span
                        key={idx}
                        className="font-medium text-slate-700 dark:text-white/90"
                    >
                        {segment.content}
                    </span>
                );
            }
            // Link to another concept
            return (
                <span
                    key={idx}
                    onClick={(e) => this.handleRelatedConceptClick(segment.conceptUri, e)}
                    className="text-accent-teal dark:text-accent hover:underline cursor-pointer"
                >
                    {segment.content}
                </span>
            );
        });
    }

    /**
     * Generate Markdown text from the vocabulary
     */
    private generateMarkdown(): string {
        const { vocabulary } = this.props;
        const concepts = vocabulary.getConcepts();

        if (concepts.length === 0) {
            return '# Vocabulary\n\n_No concepts defined._\n';
        }

        // Sort concepts alphabetically by prefLabel
        const sortedConcepts = [...concepts].sort((a, b) =>
            a.prefLabel.localeCompare(b.prefLabel)
        );

        let md = '# Vocabulary\n\n';

        for (const concept of sortedConcepts) {
            md += `## ${concept.prefLabel}\n\n`;
            md += `**URI:** \`${concept.uri}\`\n\n`;

            if (concept.definition && concept.definition !== 'No definition provided') {
                md += `**Definition:** ${concept.definition}\n\n`;
            }

            if (concept.broader.length > 0) {
                const broaderLabels = concept.broader.map(uri => {
                    const broaderConcept = vocabulary.getConcept(uri);
                    return broaderConcept.prefLabel;
                }).join(', ');
                md += `**Broader:** ${broaderLabels}\n\n`;
            }

            if (concept.narrower.length > 0) {
                const narrowerLabels = concept.narrower.map(uri => {
                    const narrowerConcept = vocabulary.getConcept(uri);
                    return narrowerConcept.prefLabel;
                }).join(', ');
                md += `**Narrower:** ${narrowerLabels}\n\n`;
            }

            md += '---\n\n';
        }

        return md;
    }

    /**
     * Copy Markdown to clipboard
     */
    private handleCopy = async () => {
        const markdown = this.generateMarkdown();
        try {
            await navigator.clipboard.writeText(markdown);
            this.setState({ copyFeedback: 'Copied!' });
            setTimeout(() => this.setState({ copyFeedback: null }), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            this.setState({ copyFeedback: 'Failed to copy' });
            setTimeout(() => this.setState({ copyFeedback: null }), 2000);
        }
    }

    /**
     * Download as .md file
     */
    private handleDownload = () => {
        const markdown = this.generateMarkdown();
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vocabulary.md';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Handle click on a concept - selects it and scrolls the cards panel
     */
    private handleConceptClick = (concept: Concept) => {
        this.props.onConceptSelected?.(concept);

        // Scroll to the concept card
        const sortedConcepts = this.getSortedConcepts();
        if (sortedConcepts.length <= VIRTUALIZATION_THRESHOLD) {
            // Small vocabulary: use ref-based scrolling
            const cardRef = this.conceptCardRefs.get(concept.uri);
            if (cardRef) {
                cardRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            // Large vocabulary: calculate scroll position for virtualized list
            const targetIndex = sortedConcepts.findIndex(c => c.uri === concept.uri);
            if (targetIndex !== -1 && this.cardsContainerRef.current) {
                const scrollTop = targetIndex * CONCEPT_CARD_HEIGHT;
                this.cardsContainerRef.current.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth'
                });
            }
        }
    }

    /**
     * Copy text to clipboard with visual feedback
     */
    private handleCopyLabel = async (text: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            // Brief visual feedback via the button itself (opacity change handled by CSS)
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    }

    /**
     * Get all ancestor URIs for a concept (for expanding tree path).
     */
    private getAncestorUris(uri: string): string[] {
        const { vocabulary } = this.props;
        const ancestors: string[] = [];
        const visited = new Set<string>();

        const collectAncestors = (conceptUri: string) => {
            if (visited.has(conceptUri)) return;
            visited.add(conceptUri);

            const concept = vocabulary.getConcept(conceptUri);
            for (const broaderUri of concept.broader) {
                ancestors.push(broaderUri);
                collectAncestors(broaderUri);
            }
        };

        collectAncestors(uri);
        return ancestors;
    }

    /**
     * Handle click on a related concept link (broader/narrower)
     * Scrolls to the target concept and selects it.
     * In hierarchical mode, expands ancestor nodes first.
     */
    private handleRelatedConceptClick = (uri: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const { vocabulary } = this.props;
        const { viewMode } = this.state;
        const concept = vocabulary.getConcept(uri);
        this.props.onConceptSelected?.(concept);

        // Helper to scroll to the concept card
        const scrollToCard = () => {
            const sortedConcepts = this.getSortedConcepts();
            if (sortedConcepts.length <= VIRTUALIZATION_THRESHOLD) {
                // Small vocabulary: use ref-based scrolling
                const cardRef = this.conceptCardRefs.get(uri);
                if (cardRef) {
                    cardRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else {
                // Large vocabulary: calculate scroll position for virtualized list
                const targetIndex = sortedConcepts.findIndex(c => c.uri === uri);
                if (targetIndex !== -1 && this.cardsContainerRef.current) {
                    const scrollTop = targetIndex * CONCEPT_CARD_HEIGHT;
                    this.cardsContainerRef.current.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth'
                    });
                }
            }
        };

        if (viewMode === 'hierarchical') {
            // Expand all ancestors to make the concept visible in the tree
            const ancestors = this.getAncestorUris(uri);
            if (ancestors.length > 0) {
                this.setState(state => {
                    const expandedNodes = new Set(state.expandedNodes);
                    ancestors.forEach(ancestorUri => expandedNodes.add(ancestorUri));
                    return { expandedNodes };
                }, () => {
                    // After state update, scroll to the concept in both tree and cards
                    setTimeout(() => {
                        // Scroll tree to show the concept
                        const treeRef = this.conceptRefs.get(uri);
                        if (treeRef) {
                            treeRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }
                        // Scroll cards panel
                        scrollToCard();
                    }, 50);
                });
            } else {
                // No ancestors, scroll directly
                const treeRef = this.conceptRefs.get(uri);
                if (treeRef) {
                    treeRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                scrollToCard();
            }
        } else {
            // Alphabetical mode: just scroll the cards panel
            scrollToCard();
        }
    }

    /**
     * Render a single concept card
     */
    private renderConceptCard(concept: Concept, termMap: Map<string, string>): React.ReactElement {
        const { vocabulary, selectedConceptUri } = this.props;
        const isSelected = selectedConceptUri === concept.uri;
        const isRoot = concept.broader.length === 0;

        // Get detailed label and definition info from the store
        const labels = this.getLabels(concept.uri);
        const definitions = this.getDefinitions(concept.uri);

        // Find the primary prefLabel (used for the title) - prefer English
        const prefLabels = labels.filter(l => l.typeUri === SKOS.prefLabel);
        const englishPrefLabel = prefLabels.find(l => l.language === 'en');
        const primaryLabel = englishPrefLabel || prefLabels[0] || { value: concept.prefLabel, typeUri: SKOS.prefLabel, typeLabel: this.getPropertyLabel(SKOS.prefLabel) };
        // Group other labels by type
        const otherLabels = labels.filter(l => l !== primaryLabel);
        const labelsByType = new Map<string, LabelInfo[]>();
        for (const label of otherLabels) {
            const existing = labelsByType.get(label.typeUri) || [];
            existing.push(label);
            labelsByType.set(label.typeUri, existing);
        }

        // Group definitions by type
        const definitionsByType = new Map<string, DefinitionInfo[]>();
        for (const def of definitions) {
            const existing = definitionsByType.get(def.typeUri) || [];
            existing.push(def);
            definitionsByType.set(def.typeUri, existing);
        }

        // Helper to capitalize first letter
        const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

        // Helper to render a label value with optional language tag
        const renderLabelValue = (label: LabelInfo, idx: number) => (
            <span key={idx}>
                {idx > 0 && ', '}
                {label.value}
                {label.language && (
                    <span className="ml-1 px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[9px]">
                        @{label.language}
                    </span>
                )}
            </span>
        );

        return (
            <div
                key={concept.uri}
                ref={(el) => { if (el) this.conceptCardRefs.set(concept.uri, el); }}
                onClick={() => this.handleConceptClick(concept)}
                className={`
                    p-4 mb-3 rounded-lg border cursor-pointer transition-colors duration-150 scroll-mt-2
                    ${isSelected
                        ? 'bg-accent-teal/10 border-accent-teal dark:bg-accent/20 dark:border-accent'
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100 dark:bg-white/5 dark:border-white/10 dark:hover:bg-white/10'
                    }
                `}
            >
                {/* Concept Name */}
                <div className="flex items-center gap-2 mb-2 group/title">
                    <h3 className={`
                        text-sm font-semibold m-0
                        ${isSelected ? 'text-accent-teal dark:text-accent' : 'text-slate-800 dark:text-white'}
                    `}>
                        {primaryLabel.value}
                    </h3>
                    {primaryLabel.language && (
                        <span className="px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[9px]">
                            @{primaryLabel.language}
                        </span>
                    )}
                    {isRoot && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-white/15 text-slate-500 dark:text-white/50 uppercase tracking-wide">
                            Root
                        </span>
                    )}
                    <button
                        onClick={(e) => this.handleCopyLabel(primaryLabel.value, e)}
                        className="opacity-0 group-hover/title:opacity-100 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-white/20 text-slate-400 dark:text-white/40 hover:text-slate-600 dark:hover:text-white/70 transition-all duration-150 cursor-pointer active:scale-90"
                        title="Copy label"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                    </button>
                </div>

                {/* Broader Hierarchy Breadcrumbs */}
                {(() => {
                    const hierarchies = this.getBroaderHierarchies(concept.uri);
                    if (hierarchies.length === 0) return null;

                    return (
                        <div className="mb-2 -mt-1">
                            {hierarchies.map((path, pathIdx) => (
                                <div key={pathIdx} className="flex items-center flex-wrap gap-0.5 text-[10px] text-slate-400 dark:text-white/40">
                                    {path.map((ancestor, idx) => (
                                        <span key={ancestor.uri} className="flex items-center">
                                            {idx > 0 && (
                                                <svg className="w-3 h-3 mx-0.5 text-slate-300 dark:text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M9 5l7 7-7 7" />
                                                </svg>
                                            )}
                                            <span
                                                onClick={(e) => this.handleRelatedConceptClick(ancestor.uri, e)}
                                                className="hover:text-accent-teal dark:hover:text-accent cursor-pointer hover:underline"
                                            >
                                                {ancestor.label}
                                            </span>
                                        </span>
                                    ))}
                                    {path.length > 0 && (
                                        <svg className="w-3 h-3 mx-0.5 text-slate-300 dark:text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 5l7 7-7 7" />
                                        </svg>
                                    )}
                                    <span className="text-slate-500 dark:text-white/50 font-medium">
                                        {primaryLabel.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {/* Alternative labels grouped by type */}
                {labelsByType.size > 0 && (
                    <div className="mb-2">
                        {Array.from(labelsByType.entries()).map(([typeUri, labelsOfType]) => (
                            <div key={typeUri} className="text-[11px] text-slate-600 dark:text-white/70 mb-1">
                                <span className="font-semibold text-slate-500 dark:text-white/50">
                                    {capitalize(labelsOfType[0].typeLabel)}{labelsOfType.length > 1 ? 's' : ''}:
                                </span>{' '}
                                {labelsOfType.map((label, idx) => renderLabelValue(label, idx))}
                            </div>
                        ))}
                    </div>
                )}

                {/* URI */}
                <div className="text-[10px] text-slate-400 dark:text-white/40 font-mono mb-2 truncate" title={concept.uri}>
                    {concept.uri}
                </div>

                {/* Definitions grouped by type */}
                {definitionsByType.size > 0 && (
                    <div className="mb-2">
                        {Array.from(definitionsByType.entries()).map(([typeUri, defsOfType]) => (
                            <div key={typeUri} className="mb-2">
                                {defsOfType.map((def, idx) => (
                                    <div key={idx} className="text-[11px] text-slate-600 dark:text-white/70 mb-1">
                                        <span className="font-semibold text-slate-500 dark:text-white/50">
                                            {capitalize(def.typeLabel)}:
                                        </span>{' '}
                                        {this.renderDefinitionWithLinks(def.value, termMap, concept.uri)}
                                        {def.language && (
                                            <span className="ml-1 px-1 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[9px]">
                                                @{def.language}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}

                {/* Relationships */}
                {(concept.broader.length > 0 || concept.narrower.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-white/10">
                        {concept.broader.length > 0 && (
                            <div className="text-[11px] text-slate-600 dark:text-white/70 mb-1.5">
                                <span className="font-semibold text-slate-500 dark:text-white/50">Broader: </span>
                                {concept.broader.map((uri, idx) => {
                                    const broaderConcept = vocabulary.getConcept(uri);
                                    return (
                                        <span key={uri}>
                                            {idx > 0 && ', '}
                                            <span
                                                onClick={(e) => this.handleRelatedConceptClick(uri, e)}
                                                className="text-accent-teal dark:text-accent hover:underline cursor-pointer"
                                            >
                                                {broaderConcept.prefLabel}
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {concept.narrower.length > 0 && (
                            <div className="text-[11px] text-slate-600 dark:text-white/70">
                                <span className="font-semibold text-slate-500 dark:text-white/50">Narrower: </span>
                                {concept.narrower.map((uri, idx) => {
                                    const narrowerConcept = vocabulary.getConcept(uri);
                                    return (
                                        <span key={uri}>
                                            {idx > 0 && ', '}
                                            <span
                                                onClick={(e) => this.handleRelatedConceptClick(uri, e)}
                                                className="text-accent-teal dark:text-accent hover:underline cursor-pointer"
                                            >
                                                {narrowerConcept.prefLabel}
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Get all broader hierarchies (paths from root to concept).
     * Returns an array of paths, where each path is an array of {uri, label} from root to parent.
     * Limited depth to prevent infinite loops in cyclic graphs.
     */
    private getBroaderHierarchies(conceptUri: string, maxDepth: number = 10): Array<Array<{ uri: string; label: string }>> {
        const { vocabulary } = this.props;
        const paths: Array<Array<{ uri: string; label: string }>> = [];

        const buildPaths = (uri: string, currentPath: Array<{ uri: string; label: string }>, visited: Set<string>) => {
            if (visited.has(uri) || currentPath.length >= maxDepth) {
                // Cycle detected or max depth reached, store the current path
                if (currentPath.length > 0) {
                    paths.push([...currentPath]);
                }
                return;
            }

            const concept = vocabulary.getConcept(uri);
            const broader = concept.broader;

            if (broader.length === 0) {
                // Reached a root concept, store the path
                if (currentPath.length > 0) {
                    paths.push([...currentPath]);
                }
            } else {
                // Continue up the hierarchy for each broader concept
                for (const broaderUri of broader) {
                    const broaderConcept = vocabulary.getConcept(broaderUri);
                    // Get English label if available
                    const labels = this.getLabels(broaderUri);
                    const prefLabels = labels.filter(l => l.typeUri === SKOS.prefLabel);
                    const englishLabel = prefLabels.find(l => l.language === 'en');
                    const label = englishLabel?.value || prefLabels[0]?.value || broaderConcept.prefLabel;

                    const newVisited = new Set(visited);
                    newVisited.add(uri);
                    buildPaths(broaderUri, [{ uri: broaderUri, label }, ...currentPath], newVisited);
                }
            }
        };

        buildPaths(conceptUri, [], new Set());
        return paths;
    }

    /**
     * Build visible tree nodes for hierarchical view.
     * Only includes expanded branches for lazy rendering.
     */
    private buildVisibleTree(): Array<{ concept: Concept; depth: number; isExpanded: boolean; hasChildren: boolean }> {
        const { vocabulary } = this.props;
        const { expandedNodes } = this.state;
        const result: Array<{ concept: Concept; depth: number; isExpanded: boolean; hasChildren: boolean }> = [];

        const schemeInfo = this.getConceptSchemeInfo();
        const topConceptUris = schemeInfo?.topConcepts.map(tc => tc.uri) || [];

        // If no top concepts defined, use concepts with no broader relations
        const concepts = vocabulary.getConcepts();
        let rootUris: string[];

        if (topConceptUris.length > 0) {
            rootUris = topConceptUris;
        } else {
            // Try to find concepts with no broader relations
            const conceptsWithoutBroader = concepts.filter(c => c.broader.length === 0);
            if (conceptsWithoutBroader.length > 0) {
                rootUris = conceptsWithoutBroader.map(c => c.uri);
            } else {
                // Fallback: show all concepts at root level (flat tree)
                rootUris = concepts.map(c => c.uri);
            }
        }

        const addNode = (uri: string, depth: number) => {
            const concept = vocabulary.getConcept(uri);
            const hasChildren = concept.narrower.length > 0;
            const isExpanded = expandedNodes.has(uri);

            result.push({ concept, depth, isExpanded, hasChildren });

            if (isExpanded && hasChildren) {
                // Sort children alphabetically
                const sortedChildren = [...concept.narrower].sort((a, b) => {
                    const aLabel = vocabulary.getConcept(a).prefLabel;
                    const bLabel = vocabulary.getConcept(b).prefLabel;
                    return aLabel.localeCompare(bLabel);
                });

                for (const childUri of sortedChildren) {
                    addNode(childUri, depth + 1);
                }
            }
        };

        // Sort root concepts alphabetically
        const sortedRoots = [...rootUris].sort((a, b) => {
            const aLabel = vocabulary.getConcept(a).prefLabel;
            const bLabel = vocabulary.getConcept(b).prefLabel;
            return aLabel.localeCompare(bLabel);
        });

        for (const uri of sortedRoots) {
            addNode(uri, 0);
        }

        return result;
    }

    /**
     * Render hierarchical tree content (without outer wrapper).
     */
    private renderHierarchyContent(): React.ReactNode {
        const { selectedConceptUri } = this.props;
        const visibleTree = this.buildVisibleTree();

        return (
            <>
                {/* Header */}
                <div className="px-3 pt-3 pb-2 border-b border-slate-200 dark:border-white/10 flex-shrink-0">
                    <div className="text-[10px] text-slate-400 dark:text-white/40 uppercase tracking-wider font-medium">
                        Concept Hierarchy
                    </div>
                </div>

                {/* Tree content */}
                <div className="flex-1 overflow-y-auto p-3">
                    {visibleTree.length === 0 ? (
                        <p className="text-[11px] text-slate-400 dark:text-white/40 italic">
                            No concepts defined.
                        </p>
                    ) : (
                        visibleTree.map(({ concept, depth, isExpanded, hasChildren }) => {
                            const isSelected = selectedConceptUri === concept.uri;
                            const indentPx = depth * 16;

                            return (
                                <div
                                    key={concept.uri}
                                    ref={(el) => { if (el) this.conceptRefs.set(concept.uri, el); }}
                                    style={{ marginLeft: indentPx }}
                                    className="mb-0.5"
                                >
                                    <div className={`flex items-center gap-1.5 py-1 px-1.5 rounded transition-colors ${
                                        isSelected
                                            ? 'bg-accent-teal/10 dark:bg-accent/20'
                                            : 'hover:bg-slate-50 dark:hover:bg-white/5'
                                    }`}>
                                        {hasChildren ? (
                                            <button
                                                onClick={() => this.toggleNode(concept.uri)}
                                                className="w-4 h-4 flex items-center justify-center text-slate-400 dark:text-white/40 hover:text-slate-600 dark:hover:text-white/70 transition-colors"
                                            >
                                                <svg
                                                    width="10" height="10" viewBox="0 0 10 10"
                                                    className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                                >
                                                    <path d="M3,1 L7,5 L3,9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                                </svg>
                                            </button>
                                        ) : (
                                            <div className="w-4" />
                                        )}
                                        <span
                                            onClick={() => this.handleConceptClick(concept)}
                                            className={`text-[11px] cursor-pointer transition-colors truncate ${
                                                isSelected
                                                    ? 'text-accent-teal dark:text-accent font-medium'
                                                    : 'text-slate-700 dark:text-white/80 hover:text-accent-teal dark:hover:text-accent'
                                            }`}
                                            title={concept.prefLabel}
                                        >
                                            {concept.prefLabel}
                                        </span>
                                        {hasChildren && (
                                            <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                                                ({concept.narrower.length})
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </>
        );
    }

    /**
     * Render flat list - virtualized for large vocabularies, normal for small ones.
     */
    private renderVirtualizedList(): React.ReactNode {
        const sortedConcepts = this.getSortedConcepts();
        const termMap = this.termMap || new Map();

        if (sortedConcepts.length === 0) {
            return (
                <div className="flex-1 p-6 overflow-y-auto">
                    <p className="text-[11px] text-slate-400 dark:text-white/40 italic">
                        No concepts defined in this vocabulary.
                    </p>
                </div>
            );
        }

        // For small vocabularies, render normally without virtualization
        if (sortedConcepts.length <= VIRTUALIZATION_THRESHOLD) {
            return (
                <div ref={this.cardsContainerRef} className="flex-1 p-6 overflow-y-auto">
                    {sortedConcepts.map(concept => this.renderConceptCard(concept, termMap))}
                </div>
            );
        }

        // For large vocabularies, use virtualization
        const { startIndex, endIndex } = this.getVisibleRange();
        const totalHeight = sortedConcepts.length * CONCEPT_CARD_HEIGHT;

        return (
            <div
                ref={this.cardsContainerRef}
                className="flex-1 overflow-y-auto"
                onScroll={this.handleScroll}
            >
                <div style={{ height: totalHeight, position: 'relative' }}>
                    {sortedConcepts.slice(startIndex, endIndex).map((concept, idx) => (
                        <div
                            key={concept.uri}
                            style={{
                                position: 'absolute',
                                top: (startIndex + idx) * CONCEPT_CARD_HEIGHT,
                                left: 0,
                                right: 0,
                                padding: '12px 24px'
                            }}
                        >
                            {this.renderConceptCard(concept, termMap)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    /**
     * Render view mode tabs above the sidebar.
     */
    private renderViewModeTabs(): React.ReactNode {
        const { viewMode } = this.state;

        const tabBaseClass = "flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors cursor-pointer border-b-2";
        const activeClass = "text-accent-teal dark:text-accent border-accent-teal dark:border-accent bg-accent-teal/5 dark:bg-accent/10";
        const inactiveClass = "text-slate-500 dark:text-white/50 border-transparent hover:text-slate-700 dark:hover:text-white/70 hover:bg-slate-50 dark:hover:bg-white/5";

        return (
            <div className="flex border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-white/[0.02]">
                {/* Alphabetical Tab */}
                <button
                    onClick={() => this.setState({ viewMode: 'alphabetical' })}
                    className={`${tabBaseClass} ${viewMode === 'alphabetical' ? activeClass : inactiveClass}`}
                    title="Alphabetical Index"
                >
                    {/* A-Z Sort Icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h10" />
                        <path d="M3 12h7" />
                        <path d="M3 18h4" />
                        <text x="17" y="10" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">A</text>
                        <text x="17" y="20" fontSize="8" fill="currentColor" stroke="none" fontWeight="600">Z</text>
                        <path d="M21 7v10" />
                        <path d="M18 17l3 3 3-3" />
                    </svg>
                    <span>A-Z</span>
                </button>

                {/* Hierarchy Tab */}
                <button
                    onClick={() => this.setState({ viewMode: 'hierarchical' })}
                    className={`${tabBaseClass} ${viewMode === 'hierarchical' ? activeClass : inactiveClass}`}
                    title="Hierarchical Tree"
                >
                    {/* Tree/Hierarchy Icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="6" height="6" rx="1" />
                        <rect x="15" y="8" width="6" height="5" rx="1" />
                        <rect x="15" y="16" width="6" height="5" rx="1" />
                        <path d="M9 6h3v8.5a.5.5 0 0 0 .5.5H15" />
                        <path d="M12 6v12.5a.5.5 0 0 0 .5.5H15" />
                    </svg>
                    <span>Tree</span>
                </button>
            </div>
        );
    }

    render() {
        const { vocabulary } = this.props;
        const { termMapReady, viewMode } = this.state;

        // Show loading state while building index
        if (!termMapReady) {
            return (
                <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-surface-dark">
                    <div className="text-slate-600 dark:text-white/70 mb-4 text-sm">
                        Building vocabulary index...
                    </div>
                    <div className="w-48 h-1.5 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent-teal dark:bg-accent rounded-full"
                            style={{ width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }}
                        />
                    </div>
                </div>
            );
        }

        const concepts = vocabulary.getConcepts();
        const schemeInfo = this.getConceptSchemeInfo();
        const alphabeticalIndex = this.alphabeticalIndex || new Map();

        // Helper to format date strings
        const formatDate = (dateStr?: string): string | null => {
            if (!dateStr) return null;
            try {
                const date = new Date(dateStr);
                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            } catch {
                return dateStr;
            }
        };

        return (
            <div className="h-full flex flex-col bg-white dark:bg-surface-dark">
                {/* Vocabulary Header */}
                <div className="px-6 pt-6 pb-5 border-b border-slate-200 dark:border-white/10 bg-gradient-to-b from-slate-50 to-white dark:from-white/[0.03] dark:to-transparent">
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-accent-teal/10 dark:bg-accent/20 text-accent-teal dark:text-accent uppercase tracking-wider font-medium">
                                    Concept Scheme
                                </span>
                                <span className="text-[10px] text-slate-400 dark:text-white/40">
                                    {concepts.length.toLocaleString()} concept{concepts.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <h1 className="text-xl font-semibold text-slate-800 dark:text-white m-0">
                                {schemeInfo?.label || 'Vocabulary'}
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Export buttons */}
                            <div className="flex gap-2">
                                <button
                                    onClick={this.handleCopy}
                                    className="px-3 py-[5px] rounded-[5px] cursor-pointer text-[11px] font-medium transition-all duration-150 font-sans border border-slate-200 dark:border-transparent bg-slate-100 dark:bg-white/[0.08] text-slate-600 dark:text-white/75 hover:bg-slate-200 hover:border-slate-300 dark:hover:bg-white/[0.15] active:scale-[0.98]"
                                >
                                    {this.state.copyFeedback || 'Copy Markdown'}
                                </button>
                                <button
                                    onClick={this.handleDownload}
                                    className="px-3 py-[5px] rounded-[5px] cursor-pointer text-[11px] font-medium transition-all duration-150 font-sans border border-slate-200 dark:border-transparent bg-slate-100 dark:bg-white/[0.08] text-slate-600 dark:text-white/75 hover:bg-slate-200 hover:border-slate-300 dark:hover:bg-white/[0.15] active:scale-[0.98]"
                                >
                                    Download .md
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    {schemeInfo?.description && (
                        <p className="text-[12px] text-slate-600 dark:text-white/70 m-0 mb-4 leading-relaxed max-w-3xl">
                            {schemeInfo.description}
                        </p>
                    )}

                    {/* Top Concepts as chips */}
                    {schemeInfo && schemeInfo.topConcepts.length > 0 && (
                        <div className="mb-3">
                            <div className="text-[10px] text-slate-400 dark:text-white/40 uppercase tracking-wider mb-2 font-medium">
                                Top Concepts
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {schemeInfo.topConcepts.map((tc) => (
                                    <span
                                        key={tc.uri}
                                        onClick={(e) => this.handleRelatedConceptClick(tc.uri, e)}
                                        className="px-2 py-1 text-[11px] rounded-full bg-white dark:bg-white/10 border border-slate-200 dark:border-white/15 text-slate-600 dark:text-white/80 hover:border-accent-teal dark:hover:border-accent hover:text-accent-teal dark:hover:text-accent cursor-pointer transition-colors duration-150"
                                    >
                                        {tc.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Metadata footer */}
                    {schemeInfo && (schemeInfo.created || schemeInfo.modified || schemeInfo.creator || schemeInfo.publisher || schemeInfo.license || schemeInfo.source) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-400 dark:text-white/40 pt-3 border-t border-slate-200/50 dark:border-white/5">
                            {schemeInfo.created && (
                                <span>Created {formatDate(schemeInfo.created)}</span>
                            )}
                            {schemeInfo.modified && (
                                <span>Modified {formatDate(schemeInfo.modified)}</span>
                            )}
                            {schemeInfo.creator && (
                                <span>By {schemeInfo.creator}</span>
                            )}
                            {schemeInfo.publisher && (
                                <span>Published by {schemeInfo.publisher}</span>
                            )}
                            {schemeInfo.license && (
                                <span>License: {schemeInfo.license}</span>
                            )}
                            {schemeInfo.source && (
                                <span>Source: {schemeInfo.source}</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Main content: Index/Tree sidebar + Concept Cards */}
                <div className="flex flex-1 min-h-0">
                    {/* Left: Tabs + Term Index or Hierarchy Tree */}
                    <div className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-white/10 flex flex-col">
                        {/* View Mode Tabs */}
                        {this.renderViewModeTabs()}

                        {/* Sidebar Content */}
                        {viewMode === 'alphabetical'
                            ? this.renderTermIndexContent(alphabeticalIndex)
                            : this.renderHierarchyContent()
                        }
                    </div>

                    {/* Right: Concept Cards */}
                    {this.renderVirtualizedList()}
                </div>
            </div>
        );
    }
}

export { ConceptSchemeViewer };
